/**
 * Oracle dashboard templates (S7, remediated S7-R). SQL adapted from the
 * same V$/DBA_ views the inline Session Browser (`electron/oracle-sessions/sessionService.ts`),
 * Instance Monitor (`electron/oracle-instance/instanceService.ts`) and
 * Tablespace Manager (`electron/oracle-tablespaces/`) already query in
 * production. Every template here reads DBA_ or V$ dictionary views, so
 * every description carries the same access-grant note (M17).
 *
 * Grew four templates for S21 starter-pack coverage (session_saturation,
 * buffer_cache_hit_ratio, long_running_operations, top_segments) — all
 * live-verified against the real-DB e2e server (jamsql@FREEPDB1, PDB
 * topology; V$RESOURCE_LIMIT was rejected for session saturation because it
 * returns 0 rows inside a PDB).
 *
 * Stat templates QUOTE their alias lowercase (`AS "x"`): Oracle folds
 * unquoted aliases to UPPERCASE, oracledb returns those keys verbatim
 * (`oracleAdapter.ts` builds columns straight from `result.metaData`), and
 * the stat lookup (`statFormat.ts` `readNumericColumn`, `column in row`) is
 * case-sensitive on `statValueColumn` — an unquoted alias renders every stat
 * tile as "column not found".
 */
import type { DashboardTemplate } from '../templateTypes';

export const oracleTemplates: DashboardTemplate[] = [
  {
    id: 'oracle.sessions.active_sessions',
    engine: 'oracle',
    title: 'Active sessions',
    description: 'User sessions currently active, longest wait first — the first thing to check when the database feels slow. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No active user sessions right now.',
    sql: `SELECT
  s.SID,
  s.SERIAL# AS serial_number,
  s.USERNAME,
  s.STATUS,
  s.MACHINE,
  s.PROGRAM,
  s.EVENT,
  s.WAIT_CLASS,
  s.SECONDS_IN_WAIT
FROM V$SESSION s
WHERE s.TYPE = 'USER'
  AND s.STATUS = 'ACTIVE'
  AND s.SID <> TO_NUMBER(SYS_CONTEXT('USERENV','SID'))
  AND {{dashboard_filters}}
ORDER BY s.SECONDS_IN_WAIT DESC`,
  },
  {
    id: 'oracle.sessions.blocking_locks',
    engine: 'oracle',
    title: 'Blocking locks',
    description: 'Sessions holding or requesting a lock that blocks another — catch a stuck chain before it cascades into a timeout storm. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No blocking locks right now.',
    sql: `SELECT
  l.SID,
  s.USERNAME,
  l.TYPE AS lock_type,
  l.LMODE,
  l.REQUEST,
  l.BLOCK,
  s.BLOCKING_SESSION
FROM V$LOCK l
JOIN V$SESSION s ON l.SID = s.SID
WHERE (l.BLOCK <> 0 OR l.REQUEST <> 0)
  AND {{dashboard_filters}}
ORDER BY l.BLOCK DESC`,
  },
  {
    id: 'oracle.sessions.long_running_operations',
    engine: 'oracle',
    title: 'Long-running operations',
    description: 'Operations Oracle expects to run long (full scans, index builds, RMAN, stats gathering) with progress and time remaining — watch a heavy job without guessing whether it is stuck. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No long-running operations in flight right now.',
    sql: `SELECT
  sl.SID,
  sl.SERIAL# AS serial_number,
  sl.USERNAME,
  sl.OPNAME,
  sl.TARGET,
  ROUND(sl.SOFAR * 100 / NULLIF(sl.TOTALWORK, 0), 1) AS pct_done,
  sl.TIME_REMAINING AS seconds_remaining,
  sl.ELAPSED_SECONDS
FROM V$SESSION_LONGOPS sl
WHERE sl.TOTALWORK > 0
  AND sl.SOFAR < sl.TOTALWORK
  AND {{dashboard_filters}}
ORDER BY sl.TIME_REMAINING DESC`,
  },
  {
    id: 'oracle.performance.top_sql',
    engine: 'oracle',
    title: 'Top SQL by elapsed time',
    description: 'Cached statements ordered by total elapsed time — the queries most worth tuning first. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'performance',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No cached SQL statements from user schemas yet.',
    sql: `SELECT
  SQL_ID,
  SUBSTR(SQL_TEXT, 1, 200) AS sql_text,
  EXECUTIONS,
  ELAPSED_TIME,
  CASE WHEN EXECUTIONS > 0 THEN ROUND(ELAPSED_TIME / EXECUTIONS) ELSE 0 END AS elapsed_per_exec
FROM V$SQL
WHERE PARSING_SCHEMA_NAME NOT IN ('SYS', 'SYSTEM', 'DBSNMP', 'SYSMAN')
  AND {{dashboard_filters}}
ORDER BY ELAPSED_TIME DESC
FETCH FIRST 20 ROWS ONLY`,
  },
  {
    id: 'oracle.performance.wait_class_summary',
    engine: 'oracle',
    title: 'Wait class summary',
    description: 'Active sessions grouped by wait class — the dominant class tells you what the instance is starving on. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'performance',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No sessions are currently waiting.',
    sql: `SELECT
  WAIT_CLASS,
  COUNT(*) AS session_count,
  SUM(SECONDS_IN_WAIT) AS total_seconds_in_wait
FROM V$SESSION
WHERE TYPE = 'USER'
  AND STATUS = 'ACTIVE'
  AND WAIT_CLASS IS NOT NULL
  AND WAIT_CLASS != 'Idle'
  AND SID <> TO_NUMBER(SYS_CONTEXT('USERENV','SID'))
  AND {{dashboard_filters}}
GROUP BY WAIT_CLASS
ORDER BY total_seconds_in_wait DESC`,
  },
  {
    id: 'oracle.performance.buffer_cache_hit_ratio',
    engine: 'oracle',
    title: 'Buffer cache hit ratio',
    description: 'Buffer-cache hit ratio per the documented V$SYSSTAT formula (1 - physical reads cache / (consistent gets from cache + db block gets from cache)), cumulative since instance startup — shows no value until the cache has activity, and a low ratio does not by itself prove an undersized cache. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'performance',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'buffer_cache_hit_pct',
    expectedShape: 'scalar',
    // Oracle's documented buffer-cache formula on V$SYSSTAT:
    //   1 - 'physical reads cache' / ('consistent gets from cache' + 'db block gets from cache')
    // All three counters ALWAYS exist, so the cross join ALWAYS returns
    // exactly one row — "0 rows" can never happen. A zero/NULL denominator
    // (no cache activity yet) yields NULL honestly instead of a made-up 100,
    // and GREATEST(0, …) clamps counter-burst artifacts so the percentage
    // can't go negative. Alias quoted lowercase — see the file header note.
    emptyStateCopy: 'Always returns one row — the cumulative buffer-cache hit ratio since instance startup (no value until the cache has activity).',
    sql: `SELECT
  CASE
    WHEN NVL(cg.VALUE, 0) + NVL(bg.VALUE, 0) = 0 THEN NULL
    ELSE ROUND(GREATEST(0, 1 - pr.VALUE / (cg.VALUE + bg.VALUE)) * 100, 2)
  END AS "buffer_cache_hit_pct"
FROM
  (SELECT VALUE FROM V$SYSSTAT WHERE NAME = 'physical reads cache') pr,
  (SELECT VALUE FROM V$SYSSTAT WHERE NAME = 'consistent gets from cache') cg,
  (SELECT VALUE FROM V$SYSSTAT WHERE NAME = 'db block gets from cache') bg
WHERE {{dashboard_filters}}`,
  },
  {
    id: 'oracle.performance.session_saturation',
    engine: 'oracle',
    title: 'Session saturation',
    description: 'User sessions visible in this container as a share of the SESSIONS limit effective in the current container (inside a PDB that is the PDB limit) — at the CDB root or on a non-CDB the limit also covers background and recursive sessions, so logins can start failing with ORA-00018 before this reaches 100%. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'performance',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'sessions_used_pct',
    expectedShape: 'scalar',
    // Counts V$SESSION rather than reading V$RESOURCE_LIMIT: inside a PDB
    // (the normal service-name topology) V$RESOURCE_LIMIT returns 0 rows —
    // live-verified against FREEPDB1. The COUNT/param cross join ALWAYS
    // returns exactly one row, so "0 rows" can never happen. V$PARAMETER
    // 'sessions' is the CURRENT CONTAINER's effective limit. Aliases quoted
    // lowercase — see the file header note.
    emptyStateCopy: 'Always returns one row — user sessions in this container as a share of its effective SESSIONS limit.',
    sql: `SELECT
  s.SESSION_COUNT AS "user_sessions",
  TO_NUMBER(p.VALUE) AS "max_sessions",
  ROUND(100 * s.SESSION_COUNT / NULLIF(TO_NUMBER(p.VALUE), 0), 1) AS "sessions_used_pct"
FROM
  (SELECT COUNT(*) AS SESSION_COUNT FROM V$SESSION WHERE TYPE = 'USER') s,
  (SELECT VALUE FROM V$PARAMETER WHERE NAME = 'sessions') p
WHERE {{dashboard_filters}}`,
  },
  {
    id: 'oracle.storage.tablespace_usage',
    engine: 'oracle',
    title: 'Tablespace usage',
    description: 'Allocated vs. used space per tablespace — the one closest to full is the one that will page you at 3am. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'storage',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No tablespaces found.',
    sql: `SELECT
  df.TABLESPACE_NAME AS tablespace_name,
  ROUND(df.ALLOCATED_BYTES / 1024 / 1024, 1) AS allocated_mb,
  ROUND((df.ALLOCATED_BYTES - NVL(fs.FREE_BYTES, 0)) / 1024 / 1024, 1) AS used_mb
FROM (
  SELECT TABLESPACE_NAME, SUM(BYTES) AS ALLOCATED_BYTES FROM DBA_DATA_FILES GROUP BY TABLESPACE_NAME
) df
LEFT JOIN (
  SELECT TABLESPACE_NAME, SUM(BYTES) AS FREE_BYTES FROM DBA_FREE_SPACE GROUP BY TABLESPACE_NAME
) fs ON df.TABLESPACE_NAME = fs.TABLESPACE_NAME
WHERE {{dashboard_filters}}
ORDER BY used_mb DESC`,
  },
  {
    id: 'oracle.storage.fra_usage',
    engine: 'oracle',
    title: 'Fast Recovery Area usage',
    description: 'Percentage of the Fast Recovery Area used, from its single V$RECOVERY_FILE_DEST row — if this fills up, archived-log writes and RMAN backups stall. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'storage',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'fra_used_pct',
    expectedShape: 'scalar',
    // Alias quoted lowercase — see the file header note.
    emptyStateCopy: 'No Fast Recovery Area is configured on this database.',
    sql: `SELECT
  ROUND((SPACE_USED - SPACE_RECLAIMABLE) * 100.0 / NULLIF(SPACE_LIMIT, 0), 1) AS "fra_used_pct"
FROM V$RECOVERY_FILE_DEST
WHERE SPACE_LIMIT > 0
  AND {{dashboard_filters}}`,
  },
  {
    id: 'oracle.storage.top_segments',
    engine: 'oracle',
    title: 'Top segments by size',
    description: 'Largest application segments by allocated space, with LOB data and LOB index segments attributed to their owning table and the recycle bin excluded (partitioned or subpartitioned LOB segments may still appear under their SYS_ names) — spot what is actually eating the tablespace. Scans the whole DBA_SEGMENTS dictionary view on every run and the row limit does not reduce that work, so this tile is best on a longer refresh interval. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'storage',
    fits: ['table', 'chart'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    // The DBA_LOBS join matches both the LOB data segment (SEGMENT_NAME,
    // SYS_LOB…) and its LOB index segment (INDEX_NAME, SYS_IL…) so both
    // attribute back to the owning table; a segment name can only ever match
    // one of the two per LOB row, so the OR can't fan rows out.
    emptyStateCopy: 'No application segments found — only Oracle-maintained schemas hold data here.',
    sql: `SELECT
  s.OWNER,
  NVL(l.TABLE_NAME, s.SEGMENT_NAME) AS object_name,
  s.SEGMENT_TYPE,
  ROUND(SUM(s.BYTES) / 1024 / 1024, 1) AS size_mb
FROM DBA_SEGMENTS s
JOIN DBA_USERS u ON u.USERNAME = s.OWNER AND u.ORACLE_MAINTAINED = 'N'
LEFT JOIN DBA_LOBS l ON l.OWNER = s.OWNER
  AND (l.SEGMENT_NAME = s.SEGMENT_NAME OR l.INDEX_NAME = s.SEGMENT_NAME)
WHERE s.SEGMENT_NAME NOT LIKE 'BIN$%'
  AND {{dashboard_filters}}
GROUP BY s.OWNER, NVL(l.TABLE_NAME, s.SEGMENT_NAME), s.SEGMENT_TYPE
ORDER BY size_mb DESC
FETCH FIRST 20 ROWS ONLY`,
  },
  {
    id: 'oracle.indexes.unusable_indexes',
    engine: 'oracle',
    title: 'Unusable indexes',
    description: 'Indexes (and index partitions/subpartitions) in an UNUSABLE state — every query planning to use one will either error or silently full-scan instead. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'indexes',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'Every index is valid and usable.',
    sql: `SELECT OWNER, INDEX_NAME, TABLE_NAME, STATUS
FROM DBA_INDEXES
WHERE STATUS = 'UNUSABLE'
  AND OWNER NOT IN ('SYS', 'SYSTEM')
  AND {{dashboard_filters}}
UNION ALL
SELECT ip.INDEX_OWNER AS OWNER, ip.INDEX_NAME, i.TABLE_NAME, ip.STATUS
FROM DBA_IND_PARTITIONS ip
JOIN DBA_INDEXES i ON i.OWNER = ip.INDEX_OWNER AND i.INDEX_NAME = ip.INDEX_NAME
WHERE ip.STATUS = 'UNUSABLE'
  AND ip.INDEX_OWNER NOT IN ('SYS', 'SYSTEM')
  AND {{dashboard_filters}}
UNION ALL
SELECT isub.INDEX_OWNER AS OWNER, isub.INDEX_NAME, i2.TABLE_NAME, isub.STATUS
FROM DBA_IND_SUBPARTITIONS isub
JOIN DBA_INDEXES i2 ON i2.OWNER = isub.INDEX_OWNER AND i2.INDEX_NAME = isub.INDEX_NAME
WHERE isub.STATUS = 'UNUSABLE'
  AND isub.INDEX_OWNER NOT IN ('SYS', 'SYSTEM')
  AND {{dashboard_filters}}
ORDER BY OWNER, TABLE_NAME`,
  },
  {
    id: 'oracle.jobs.failed_scheduler_jobs',
    engine: 'oracle',
    title: 'Failed scheduler jobs',
    description: 'DBMS_SCHEDULER jobs whose LAST run failed — catch broken batch/ETL jobs before someone notices stale data. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'jobs',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No scheduler jobs have failed their last run.',
    sql: `SELECT OWNER, JOB_NAME, STATUS, ERROR#, ACTUAL_START_DATE
FROM (
  SELECT
    OWNER,
    JOB_NAME,
    STATUS,
    ERROR#,
    ACTUAL_START_DATE,
    ROW_NUMBER() OVER (PARTITION BY OWNER, JOB_NAME ORDER BY LOG_ID DESC) AS rn
  FROM DBA_SCHEDULER_JOB_RUN_DETAILS
) latest
WHERE rn = 1
  AND STATUS = 'FAILED'
  AND {{dashboard_filters}}
ORDER BY ACTUAL_START_DATE DESC
FETCH FIRST 50 ROWS ONLY`,
  },
  {
    id: 'oracle.backup.rman_backup_status',
    engine: 'oracle',
    title: 'RMAN backup status',
    description: 'Recent RMAN backup jobs and their outcome — catch a failed or skipped backup before you need it. Requires SELECT_CATALOG_ROLE or SELECT ANY DICTIONARY.',
    category: 'backup',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No RMAN backup jobs recorded yet.',
    sql: `SELECT
  INPUT_TYPE,
  STATUS,
  START_TIME,
  END_TIME,
  ELAPSED_SECONDS
FROM V$RMAN_BACKUP_JOB_DETAILS
WHERE {{dashboard_filters}}
ORDER BY START_TIME DESC
FETCH FIRST 20 ROWS ONLY`,
  },
];
